import React, { useState, useEffect, useCallback } from 'react';
import { Check, Loader2, CalendarX, MapPin } from 'lucide-react';
import { supabaseUrl } from '../../lib/supabase/client';

/**
 * Öffentliche Buchungsseite (/buchen/:slug) — Gäste wählen ohne Login einen
 * freien Slot und buchen ihn direkt; daraus entsteht sofort ein Termin
 * (Outlook-Einladung folgt über den nächsten Kalender-Sync). Datenquelle ist
 * die Edge Function `buchung` (GET liefert Tage+Slots, POST bucht).
 */

const API = `${supabaseUrl}/functions/v1/buchung`;

interface Tag { datum: string; label: string; slots: string[] }
interface BuchungsDaten {
  titel: string;
  beschreibung: string | null;
  typ: string;
  objektAdresse: string | null;
  slotDauer: number;
  tage: Tag[];
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatSlot = (slot: string): string =>
  new Date(slot).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }) + ' Uhr';

const BuchungsSeite: React.FC = () => {
  const slug = window.location.pathname.split('/')[2] || '';

  const [laden, setLaden] = useState(true);
  const [daten, setDaten] = useState<BuchungsDaten | null>(null);
  const [fehlerLaden, setFehlerLaden] = useState<string | null>(null);

  const [ausgewaehlterTag, setAusgewaehlterTag] = useState<string>('');
  const [ausgewaehlterSlot, setAusgewaehlterSlot] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');

  const [sende, setSende] = useState(false);
  const [fehlerSenden, setFehlerSenden] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<{ titel: string; zeit: string } | null>(null);

  const ladeSlots = useCallback(async () => {
    if (!slug) {
      setFehlerLaden('Dieser Link ist ungültig.');
      setLaden(false);
      return;
    }
    try {
      const res = await fetch(`${API}?slug=${encodeURIComponent(slug)}`);
      const d = await res.json();
      if (d.ok) {
        setDaten(d);
        setFehlerLaden(null);
        setAusgewaehlterTag((bisher) => (bisher && d.tage.some((t: Tag) => t.datum === bisher) ? bisher : d.tage[0]?.datum || ''));
      } else {
        setDaten(null);
        setFehlerLaden(d.fehler || 'Dieser Link ist ungültig.');
      }
    } catch {
      setFehlerLaden('Verbindungsfehler — bitte laden Sie die Seite neu.');
    } finally {
      setLaden(false);
    }
  }, [slug]);

  useEffect(() => {
    ladeSlots();
  }, [ladeSlots]);

  const aktuellerTag = daten?.tage.find((t) => t.datum === ausgewaehlterTag) || null;
  const emailGueltig = EMAIL_REGEX.test(email.trim());
  const kannBuchen = !!ausgewaehlterSlot && name.trim().length > 0 && telefon.trim().length > 0 && emailGueltig;

  const buchen = async () => {
    if (!kannBuchen || !ausgewaehlterSlot) return;
    setSende(true);
    setFehlerSenden(null);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, start: ausgewaehlterSlot, name: name.trim(), email: email.trim(), telefon: telefon.trim() }),
      });
      const d = await res.json();
      if (d.ok) {
        setErgebnis({ titel: d.titel, zeit: d.zeit });
      } else {
        setFehlerSenden(d.fehler || 'Das hat leider nicht geklappt.');
        if (res.status === 409) {
          setAusgewaehlterSlot(null);
          ladeSlots();
        }
      }
    } catch {
      setFehlerSenden('Verbindungsfehler — bitte erneut versuchen.');
    } finally {
      setSende(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1d24] p-6">
      <div className="w-full max-w-lg bg-white/[0.06] border border-white/10 rounded-[2rem] backdrop-blur-xl p-10 text-center">
        <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.3em] mb-8">
          Immobilienmanagement Schwede
        </p>

        {laden && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 size={28} className="text-[#c4a77d] animate-spin" />
            <p className="text-xs font-semibold text-white/40 uppercase tracking-widest">Termine werden geladen…</p>
          </div>
        )}

        {!laden && !daten && (
          <div className="py-4">
            <CalendarX size={36} className="text-white/30 mx-auto mb-5" />
            <h1 className="text-lg font-black text-white mb-2">Link ungültig</h1>
            <p className="text-sm text-white/60 leading-relaxed">{fehlerLaden}</p>
          </div>
        )}

        {!laden && daten && ergebnis && (
          <div className="py-4">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center bg-emerald-500/20 text-emerald-400">
              <Check size={30} />
            </div>
            <h1 className="text-lg font-black text-white mb-3">Termin gebucht!</h1>
            <p className="text-sm text-white/70 leading-relaxed mb-3">{ergebnis.zeit}</p>
            <p className="text-xs text-white/50 leading-relaxed">
              Die Kalender-Einladung kommt per E-Mail an {email.trim()}.
            </p>
          </div>
        )}

        {!laden && daten && !ergebnis && (
          <>
            <h1 className="text-2xl font-black text-white mb-2">{daten.titel}</h1>
            {daten.beschreibung && (
              <p className="text-sm text-white/60 leading-relaxed mb-3">{daten.beschreibung}</p>
            )}
            {daten.objektAdresse && (
              <p className="flex items-center justify-center gap-1.5 text-xs text-white/50 mb-6">
                <MapPin size={13} /> {daten.objektAdresse}
              </p>
            )}

            {daten.tage.length === 0 && (
              <p className="text-sm text-white/60 leading-relaxed py-6">
                Aktuell sind leider keine freien Termine verfügbar.
              </p>
            )}

            {daten.tage.length > 0 && (
              <>
                <div className="flex gap-2 overflow-x-auto pb-1 mb-4" style={{ scrollbarWidth: 'none' }}>
                  {daten.tage.map((tag) => (
                    <button
                      key={tag.datum}
                      onClick={() => { setAusgewaehlterTag(tag.datum); setAusgewaehlterSlot(null); }}
                      className={`shrink-0 min-h-[44px] platz:min-h-0 flex items-center px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                        ausgewaehlterTag === tag.datum
                          ? 'bg-[#c4a77d] text-white'
                          : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                      }`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-2 mb-6">
                  {(aktuellerTag?.slots || []).map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setAusgewaehlterSlot(slot)}
                      className={`min-h-[44px] platz:min-h-0 flex items-center justify-center px-2 py-2.5 rounded-xl text-xs font-bold transition-all ${
                        ausgewaehlterSlot === slot
                          ? 'bg-[#c4a77d] text-white'
                          : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                      }`}
                    >
                      {formatSlot(slot)}
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ihr Name"
                    autoComplete="name"
                    className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/15 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40 transition-all"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ihre E-Mail-Adresse"
                    autoComplete="email"
                    className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/15 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40 transition-all"
                  />
                  <input
                    type="tel"
                    value={telefon}
                    onChange={(e) => setTelefon(e.target.value)}
                    placeholder="Ihre Telefonnummer"
                    autoComplete="tel"
                    className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/15 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40 transition-all"
                  />
                </div>

                <button
                  onClick={buchen}
                  disabled={!kannBuchen || sende}
                  className="w-full mt-5 flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-white bg-[#c4a77d] hover:bg-[#b3966a] transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sende ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Termin buchen
                </button>

                {fehlerSenden && <p className="mt-4 text-sm font-semibold text-red-400">{fehlerSenden}</p>}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BuchungsSeite;
